import React, { createContext, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { EntityTypes } from '@guryou/core';
import { useEffectOnce } from '@guryou/hooks';
import { DynamicThemeContext } from '@guryou/html-components';
import {
  getAllPublicProducts,
  getClassesForBooking,
  getPaymentOptions,
  getSubservicesForBooking,
  stateIsLoading,
  stateIsNotInitialized,
  StateStatus,
  useCurrentUser,
  widgetGetIntegration,
} from '@guryou/redux';
import { ensureWidgetIntegrationGoogleFont } from '../../utils/widgetIntegrationGoogleFonts';

const WidgetProvider = ({ children, idWidget }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { integrationId } = useParams();
  const { setMainColor } = useContext(DynamicThemeContext);
  const { user } = useCurrentUser();

  const [activeServiceId, setActiveServiceId] = useState(null);
  const [activeSubServiceId, setActiveSubServiceId] = useState(null);
  const [activeClassId, setActiveClassId] = useState(null);
  const [activeTab, setActiveTab] = useState();
  const [sessionActiveStep, setSessionActiveStep] = useState(0);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [error, setError] = useState(null);
  const [selsectedOfferForCourse, setSelsectedOfferForCourse] = useState([]);

  const { integration, videos, paymentIntent, reservationCreate, classesForBooking, subservicesForBooking } = useSelector(state => state.widget);
  const paymentOptions = useSelector(state => state.paymentOptions.get);
  const { providerId, configuration, provider, integrationType: integrationTypeFromApi } = integration.data || {};
  const { resources, memberships, plan } = provider || {};
  const {
    showEvents,
    showVideos,
    showCourses,
    showServices,
    showMemberships,
    primaryColor,
    askEmail,
    askFirstname,
    askLastname,
    fontFamily,
    fontStyle,
    askPhone,
    widgetMode,
  } = configuration || {};
  const services = subservicesForBooking;
  const courses = classesForBooking;

  //od configuration treba da se zeme
  const fontF = fontFamily ? fontFamily : 'Metropolis';
  const fontS = fontStyle ? fontStyle : 'sans-serif';

  useEffect(() => {
    if (reservationCreate.status === StateStatus.ERROR) {
      setError(reservationCreate.errors);
    }
    if (reservationCreate.status === StateStatus.LOADED) {
      if (reservationCreate.data.clientSecret) {
        const token = btoa(JSON.stringify({ ...reservationCreate.data, returnUrl: window.location.href }));
        window.location = `${process.env.REACT_APP_PAYMENT_APP_URL}/${token}`;
      }
    }
  }, [providerId, reservationCreate, setError]);

  useEffectOnce(() => {
    if (idWidget) {
      dispatch(widgetGetIntegration(idWidget));
    } else if (integrationId) {
      dispatch(widgetGetIntegration(integrationId));
    }
  });

  useEffect(() => {
    if (stateIsNotInitialized(integration) || stateIsLoading(integration)) {
      return;
    }
    const isAuthWidget = integrationTypeFromApi === 'auth' || widgetMode === 'auth';
    const isCalendarWidget = integrationTypeFromApi === 'calendar' || widgetMode === 'calendar';
    if (!isAuthWidget && !isCalendarWidget) {
      return;
    }
    // Only redirect from the standalone route — embedded widgets (idWidget only) must not hijack the route.
    if (!integrationId) {
      return;
    }
    if (isAuthWidget) {
      history.replace(`/website-integration-auth/${integrationId}`);
      return;
    }
    if (isCalendarWidget) {
      history.replace(`/website-integration-calendar/${integrationId}`);
    }
  }, [integrationTypeFromApi, widgetMode, integrationId, history, integration]);

  useEffect(() => {
    if (!providerId) {
      return;
    }

    dispatch(getSubservicesForBooking(providerId));
    dispatch(getClassesForBooking(providerId));
    dispatch(getAllPublicProducts(providerId, null, +integrationId || idWidget));
    dispatch(getPaymentOptions(providerId));

    primaryColor && setMainColor(primaryColor);
    // eslint-disable-next-line
  }, [dispatch, integrationId, primaryColor, providerId, setMainColor, idWidget]);

  useEffect(() => {
    if (!providerId) {
      return;
    }
    const service =
      (showServices && services?.data?.[0]) ||
      (showCourses && courses?.data?.[0]) ||
      (showMemberships && memberships?.[0]) ||
      (showVideos && videos.data?.[0]);

    const firstActiveTab =
      showServices && services?.data?.[0]
        ? EntityTypes.service
        : showCourses && courses?.data?.[0]
        ? EntityTypes.course
        : showMemberships && memberships?.[0]
        ? EntityTypes.membership
        : showVideos && videos.data?.[0]
        ? EntityTypes.video
        : null;

    if (service) {
      setActiveServiceId(service.id || service.data[0].id);
      setActiveTab(firstActiveTab);
    } else {
      setActiveTab(
        (showServices && EntityTypes.service && services.data?.length) ||
          (showCourses && EntityTypes.course && courses.data?.length) ||
          (showVideos && EntityTypes.video && videos.data?.length) ||
          (showMemberships && EntityTypes.membership)
      );
    }
  }, [courses.data, memberships, providerId, services.data, showCourses, showMemberships, showServices, showVideos, videos.data]);

  useEffect(() => {
    switch (activeTab) {
      case EntityTypes.service:
        setActiveServiceId(services?.data?.[0]?.id);
        break;
      case EntityTypes.course:
        setActiveServiceId(courses?.data?.[0]?.id);
        break;
      case EntityTypes.membership:
        setActiveServiceId(memberships?.[0]?.id);
        break;
      case EntityTypes.video:
        setActiveServiceId(videos?.data?.[0]?.id);
        break;
      default:
        setActiveServiceId(null);
    }
  }, [activeTab, courses, memberships, services, videos]);

  useEffect(() => {
    if (paymentIntent.status === StateStatus.LOADED) {
      if (!paymentIntent.data.amount) {
        setActiveServiceId(null);
        setActiveSubServiceId(null);
        setActiveClassId(null);
        return;
      }
      const token = btoa(JSON.stringify({ ...paymentIntent.data, returnUrl: window.location.href }));
      window.location = `${process.env.REACT_APP_PAYMENT_APP_URL}/${token}`;
    }
  }, [paymentIntent]);

  useEffect(() => {
    ensureWidgetIntegrationGoogleFont(fontF);
  }, [fontF]);

  if (
    !activeTab ||
    stateIsNotInitialized(integration) ||
    (showVideos && stateIsNotInitialized(videos)) ||
    (showServices && stateIsNotInitialized(services)) ||
    (showCourses && stateIsNotInitialized(courses)) ||
    (showEvents && stateIsNotInitialized(courses)) ||
    (showMemberships && stateIsNotInitialized(memberships)) ||
    (paymentOptions && stateIsLoading(paymentOptions)) ||
    (plan && stateIsNotInitialized(plan))
  ) {
    return null;
  }
  if (
    (stateIsLoading(integration) && !integration?.data) ||
    (showVideos && stateIsLoading(videos)) ||
    (showServices && stateIsLoading(services)) ||
    (showCourses && stateIsLoading(courses)) ||
    (showEvents && stateIsLoading(courses)) ||
    (showMemberships && stateIsLoading(memberships)) ||
    (paymentOptions && stateIsLoading(paymentOptions)) ||
    (plan && stateIsLoading(plan))
  ) {
    return (
      <div className="min-h-screen">
        {/* Header Skeleton */}
        <div className="bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-center py-3">
              <div className="flex space-x-1 bg-gray-100 rounded-lg p-2">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="w-12 h-12 bg-gray-200 rounded-md animate-pulse"></div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Content Skeleton - Widget Layout */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar Skeleton */}
            <div className="lg:w-80 flex-shrink-0">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
                <div className="flex items-center mb-4">
                  <div className="w-6 h-6 bg-gray-200 rounded animate-pulse mr-2"></div>
                  <div className="h-6 bg-gray-200 rounded w-32 animate-pulse"></div>
                </div>
                <div className="space-y-2">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="flex items-center justify-between p-4 bg-gray-100 rounded-lg animate-pulse">
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                      <div className="w-8 h-6 bg-gray-200 rounded-full"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Main Content Skeleton */}
            <div className="flex-1">
              {/* Header Section Skeleton */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gray-200 rounded-full animate-pulse mr-4"></div>
                  <div>
                    <div className="h-8 bg-gray-200 rounded w-48 mb-2 animate-pulse"></div>
                    <div className="h-4 bg-gray-200 rounded w-64 animate-pulse"></div>
                  </div>
                </div>
              </div>

              {/* Services Grid Skeleton */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                    <div className="flex items-center mb-4">
                      <div className="flex space-x-1 mr-3">
                        {[1, 2, 3, 4, 5].map(star => (
                          <div key={star} className="w-4 h-4 bg-gray-200 rounded"></div>
                        ))}
                      </div>
                      <div className="w-12 h-6 bg-gray-200 rounded"></div>
                    </div>
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center">
                        <div className="w-4 h-4 bg-gray-200 rounded mr-3"></div>
                        <div className="h-4 bg-gray-200 rounded w-20"></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="h-8 bg-gray-200 rounded w-16"></div>
                      <div className="h-10 bg-gray-200 rounded w-24"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const value = {
    resources,
    paymentOptions,
    services,
    courses,
    memberships,
    plan,
    activeSubServiceId,
    setActiveSubServiceId,
    activeServiceId,
    setActiveServiceId,
    activeClassId,
    setActiveClassId,
    error,
    setError,
    activeTab,
    setActiveTab,
    sessionActiveStep,
    setSessionActiveStep,
    selectedSessions,
    setSelectedSessions,
    selsectedOfferForCourse,
    setSelsectedOfferForCourse,
    showEvents,
    showVideos,
    showCourses,
    showServices,
    showMemberships,
    askEmail,
    askFirstname,
    askLastname,
    askPhone,
    fontF,
    fontS,
    user,
    primaryColor,
    acceptPayments: provider?.acceptPayments || false,
    onlineDiscount: provider?.onlineDiscount || false,
  };

  // Apply custom fonts globally to the widget
  const fontFamilyStyle = fontF && fontF !== 'Metropolis' ? { fontFamily: fontF } : {};

  // Apply font style (italic, bold, etc.)
  let fontStyleCSS = {};
  if (fontS && fontS !== 'sans-serif') {
    if (fontS === 'italic') {
      fontStyleCSS = { fontStyle: 'italic' };
    } else if (fontS === 'bold') {
      fontStyleCSS = { fontWeight: 'bold' };
    } else if (fontS === 'Double-struck') {
      // Double-struck is a mathematical font style, we'll use bold as approximation
      fontStyleCSS = { fontWeight: 'bold' };
    }
  }

  return (
    <div style={{ ...fontFamilyStyle, ...fontStyleCSS }}>
      <WidgetContext.Provider value={value}>{children}</WidgetContext.Provider>
    </div>
  );
};

const WidgetContext = createContext();

export { WidgetProvider, WidgetContext };
